import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Mail, Users, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LOGO_URL } from "@/data/content";

const TABS = [
  { key: "events", label: "Événements" },
  { key: "event_registrations", label: "Inscriptions événements" },
  { key: "blog", label: "Articles" },
  { key: "resources", label: "Ressources" },
  { key: "testimonials", label: "Témoignages" },
  { key: "contacts", label: "Contacts" },
  { key: "joins", label: "Inscriptions" },
  { key: "donations", label: "Dons" },
  { key: "newsletter", label: "Newsletter" },
];

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("events");
  const [data, setData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});

  const fetchTab = async (t) => {
    const endpointMap = {
      events: "/events",
      event_registrations: "/event-registrations",
      blog: "/blog",
      resources: "/resources",
      testimonials: "/testimonials",
      contacts: "/contact",
      joins: "/join",
      donations: "/donations",
      newsletter: "/newsletter",
    };
    try {
      const { data: items } = await api.get(endpointMap[t]);
      setData((d) => ({ ...d, [t]: items }));
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };

  useEffect(() => {
    if (user) {
      TABS.forEach((t) => fetchTab(t.key));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const createItem = async () => {
    const endpointMap = {
      events: "/events",
      blog: "/blog",
      resources: "/resources",
      testimonials: "/testimonials",
    };
    try {
      await api.post(endpointMap[tab], form);
      toast.success("Créé avec succès");
      setForm({});
      setShowForm(false);
      fetchTab(tab);
    } catch (e) {
      toast.error("Erreur de création");
    }
  };

  const deleteItem = async (id) => {
    if (!confirm("Supprimer cet élément ?")) return;
    const endpointMap = {
      events: `/events/${id}`,
      blog: `/blog/${id}`,
      resources: `/resources/${id}`,
      testimonials: `/testimonials/${id}`,
    };
    try {
      await api.delete(endpointMap[tab]);
      toast.success("Supprimé");
      fetchTab(tab);
    } catch {
      toast.error("Erreur");
    }
  };

  const isEditable = ["events", "blog", "resources", "testimonials"].includes(tab);
  const items = data[tab] || [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="mv-container py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MIREVSA" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-heading font-black text-lg">MIREVSA Admin</div>
              <div className="text-xs text-muted">{user.email}</div>
            </div>
          </Link>
          <button
            onClick={() => { logout(); toast.success("Déconnecté"); }}
            data-testid="admin-logout"
            className="text-sm font-medium text-[#1C1C1C] hover:text-[#0B3D91] flex items-center gap-2"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </div>

      <div className="mv-container py-10">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setShowForm(false); setForm({}); }}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-2.5 text-sm font-medium border transition-colors ${
                tab === t.key
                  ? "bg-[#0B3D91] text-white border-[#0B3D91]"
                  : "bg-white text-[#1C1C1C] border-gray-300 hover:border-[#0B3D91]"
              }`}
            >
              {t.label} {data[t.key] ? `(${data[t.key].length})` : ""}
            </button>
          ))}
        </div>

        {isEditable && (
          <div className="mb-6">
            <button
              onClick={() => setShowForm((v) => !v)}
              data-testid="admin-new-btn"
              className="btn-primary !py-2.5 !px-5 text-sm"
            >
              <Plus size={16} /> {showForm ? "Annuler" : "Nouveau"}
            </button>
          </div>
        )}

        {/* Form */}
        {isEditable && showForm && (
          <div className="bg-white border border-gray-200 p-6 mb-8" data-testid="admin-form">
            <h3 className="font-heading font-bold text-xl mb-5">Nouvel élément</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tab === "events" && (
                <>
                  <Input label="Titre" v={form.title} on={(v) => setForm({ ...form, title: v })} />
                  <Input label="Catégorie (Conférence/Croisade/Veillée/Séminaire)" v={form.category} on={(v) => setForm({ ...form, category: v })} />
                  <Input label="Date (YYYY-MM-DD)" v={form.date} on={(v) => setForm({ ...form, date: v })} />
                  <Input label="Lieu" v={form.location} on={(v) => setForm({ ...form, location: v })} />
                  <Input label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} cls="md:col-span-2" />
                  <TextArea label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} cls="md:col-span-2" />
                </>
              )}
              {tab === "blog" && (
                <>
                  <Input label="Titre" v={form.title} on={(v) => setForm({ ...form, title: v })} cls="md:col-span-2" />
                  <Input label="Catégorie (Enseignement/Prédication/Article)" v={form.category} on={(v) => setForm({ ...form, category: v })} />
                  <Input label="Auteur" v={form.author} on={(v) => setForm({ ...form, author: v })} />
                  <Input label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} cls="md:col-span-2" />
                  <TextArea label="Extrait" v={form.excerpt} on={(v) => setForm({ ...form, excerpt: v })} cls="md:col-span-2" />
                  <TextArea label="Contenu" v={form.content} on={(v) => setForm({ ...form, content: v })} cls="md:col-span-2" rows={6} />
                </>
              )}
              {tab === "resources" && (
                <>
                  <Input label="Titre" v={form.title} on={(v) => setForm({ ...form, title: v })} cls="md:col-span-2" />
                  <Input label="Catégorie (Prédication/Enseignement/Brochure/Vidéo)" v={form.category} on={(v) => setForm({ ...form, category: v })} />
                  <Input label="Auteur" v={form.author} on={(v) => setForm({ ...form, author: v })} />
                  <Input label="Date de publication (YYYY-MM-DD)" v={form.published_date} on={(v) => setForm({ ...form, published_date: v })} />
                  <Input label="Vignette / Image URL" v={form.thumbnail_url} on={(v) => setForm({ ...form, thumbnail_url: v })} />
                  <Input label="Lien externe (YouTube, article...)" v={form.external_url} on={(v) => setForm({ ...form, external_url: v })} cls="md:col-span-2" />
                  <Input label="Lien de téléchargement (PDF)" v={form.download_url} on={(v) => setForm({ ...form, download_url: v })} cls="md:col-span-2" />
                  <TextArea label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} cls="md:col-span-2" />
                </>
              )}
              {tab === "testimonials" && (
                <>
                  <Input label="Nom" v={form.name} on={(v) => setForm({ ...form, name: v })} />
                  <Input label="Lieu" v={form.location} on={(v) => setForm({ ...form, location: v })} />
                  <Input label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} cls="md:col-span-2" />
                  <TextArea label="Citation" v={form.quote} on={(v) => setForm({ ...form, quote: v })} cls="md:col-span-2" />
                </>
              )}
            </div>
            <button onClick={createItem} className="mt-5 btn-primary !py-2.5 text-sm">Créer</button>
          </div>
        )}

        {/* Data table */}
        <div className="bg-white border border-gray-200">
          {items.length === 0 ? (
            <div className="p-10 text-center text-muted text-sm">Aucun élément.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {items.map((it) => (
                <div key={it.id} className="p-5 flex items-start justify-between gap-4 hover:bg-[#F8FAFC]" data-testid={`admin-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#1C1C1C]">
                      {it.title || it.name || it.subject || it.email}
                    </div>
                    <div className="text-sm text-muted mt-1 line-clamp-2">
                      {it.description || it.excerpt || it.quote || it.message || it.cause || ""}
                    </div>
                    <div className="text-xs text-muted mt-2 flex flex-wrap gap-3">
                      {it.email && <span><Mail size={11} className="inline" /> {it.email}</span>}
                      {it.phone && <span>{it.phone}</span>}
                      {it.category && <span className="text-[#0B3D91] font-bold">{it.category}</span>}
                      {it.interest && <span className="text-[#0B3D91] font-bold">{it.interest}</span>}
                      {it.amount && <span className="text-[#0B3D91] font-bold">${it.amount} — {it.cause}</span>}
                      {it.date && <span>{it.date}</span>}
                      {it.created_at && <span>{new Date(it.created_at).toLocaleDateString("fr-FR")}</span>}
                    </div>
                  </div>
                  {isEditable && (
                    <button
                      onClick={() => deleteItem(it.id)}
                      data-testid={`admin-delete-${it.id}`}
                      className="text-red-600 hover:bg-red-50 p-2 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, v, on, cls = "" }) => (
  <div className={cls}>
    <label className="label-eyebrow">{label}</label>
    <input
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      className="mt-2 w-full border border-gray-300 px-3 py-2 focus:outline-none focus:border-[#0B3D91] text-sm"
    />
  </div>
);

const TextArea = ({ label, v, on, cls = "", rows = 3 }) => (
  <div className={cls}>
    <label className="label-eyebrow">{label}</label>
    <textarea
      rows={rows}
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      className="mt-2 w-full border border-gray-300 px-3 py-2 focus:outline-none focus:border-[#0B3D91] text-sm"
    />
  </div>
);

export default AdminDashboard;
